import { clearTeslaWithMeBrowserState } from './browser-state';

const GEO_ERROR_LABELS: Record<number, string> = {
  1: 'PERMISSION_DENIED',
  2: 'POSITION_UNAVAILABLE',
  3: 'TIMEOUT',
};

export function renderGeoDiagnostic(container: HTMLElement, onBack: () => void): void {
  let watchId: number | null = null;
  const logLines: string[] = [];

  container.innerHTML = `
    <div class="flex flex-col items-center min-h-screen bg-tesla-bg px-6 py-8 gap-6">
      <div class="w-full max-w-2xl">
        <button id="geo-back-btn"
          class="border border-tesla-border text-tesla-muted rounded-lg px-4 py-3 text-sm active:opacity-75">
          Back
        </button>
      </div>

      <div class="w-full max-w-2xl bg-tesla-surface border border-tesla-border rounded-xl p-6">
        <h1 class="text-3xl font-bold text-tesla-cyan">Browser Location Check</h1>
        <p class="text-tesla-muted mt-2">
          This page tests raw browser geolocation only. No rooms, map tiles, or peer connections.
        </p>
        <p class="text-tesla-muted mt-2">
          If this screen cannot get coordinates, the problem is outside the TeslaWithMe room flow.
        </p>
      </div>

      <div class="w-full max-w-2xl bg-tesla-surface border border-tesla-border rounded-xl p-6 space-y-4">
        <div class="flex items-center justify-between gap-4 flex-wrap">
          <label class="flex items-center gap-3 text-white">
            <input id="geo-high-accuracy" type="checkbox" class="accent-cyan-400" />
            <span>Use high accuracy</span>
          </label>
          <div class="text-tesla-muted text-sm" id="geo-permission">Permission: checking…</div>
        </div>

        <div class="flex gap-3 flex-wrap">
          <button id="geo-run-once"
            class="bg-tesla-cyan text-black font-bold rounded-lg px-5 py-3 active:opacity-75">
            Run One-Shot Check
          </button>
          <button id="geo-start-watch"
            class="border border-tesla-cyan text-tesla-cyan font-bold rounded-lg px-5 py-3 active:opacity-75">
            Start Watch
          </button>
          <button id="geo-stop-watch"
            class="border border-tesla-border text-tesla-muted rounded-lg px-5 py-3 active:opacity-75"
            disabled>
            Stop Watch
          </button>
          <button id="geo-copy-log"
            class="border border-tesla-border text-white rounded-lg px-5 py-3 active:opacity-75">
            Copy Log
          </button>
        </div>

        <div class="bg-black border border-tesla-border rounded-xl px-4 py-4">
          <div class="text-tesla-muted text-sm">Status</div>
          <div id="geo-status" class="text-white text-lg mt-1">Ready</div>
          <div id="geo-last-fix" class="text-tesla-muted text-sm mt-2">No coordinates yet.</div>
        </div>
      </div>

      <div class="w-full max-w-2xl bg-tesla-surface border border-tesla-border rounded-xl p-6">
        <div class="flex items-center justify-between gap-4">
          <h2 class="text-white font-semibold text-lg">Diagnostic Log</h2>
          <span class="text-tesla-muted text-xs">Also mirrored to the browser console</span>
        </div>
        <pre id="geo-log"
          class="mt-4 bg-black border border-tesla-border rounded-xl p-4 text-xs text-white overflow-auto whitespace-pre-wrap min-h-[280px]"></pre>
      </div>

      <footer class="w-full max-w-2xl border-t border-tesla-border pt-5 text-center">
        <button id="geo-reset-state"
          class="w-full border border-red-800 text-red-300 rounded-lg px-5 py-3 active:opacity-75">
          Reset App Storage
        </button>
      </footer>
    </div>
  `;

  const backBtn = container.querySelector('#geo-back-btn') as HTMLButtonElement;
  const highAccuracyInput = container.querySelector('#geo-high-accuracy') as HTMLInputElement;
  const permissionEl = container.querySelector('#geo-permission') as HTMLElement;
  const runOnceBtn = container.querySelector('#geo-run-once') as HTMLButtonElement;
  const startWatchBtn = container.querySelector('#geo-start-watch') as HTMLButtonElement;
  const stopWatchBtn = container.querySelector('#geo-stop-watch') as HTMLButtonElement;
  const copyLogBtn = container.querySelector('#geo-copy-log') as HTMLButtonElement;
  const resetStateBtn = container.querySelector('#geo-reset-state') as HTMLButtonElement;
  const statusEl = container.querySelector('#geo-status') as HTMLElement;
  const lastFixEl = container.querySelector('#geo-last-fix') as HTMLElement;
  const logEl = container.querySelector('#geo-log') as HTMLElement;

  const updateWatchButtons = (): void => {
    stopWatchBtn.disabled = watchId === null;
    startWatchBtn.disabled = watchId !== null;
    stopWatchBtn.classList.toggle('text-tesla-muted', watchId === null);
    stopWatchBtn.classList.toggle('text-white', watchId !== null);
    startWatchBtn.classList.toggle('opacity-50', watchId !== null);
  };

  const appendLog = (label: string, payload?: unknown): void => {
    const formatted = payload === undefined ? label : `${label} ${safeFormat(payload)}`;
    logLines.push(formatted);
    logEl.textContent = logLines.join('\n');
    logEl.scrollTop = logEl.scrollHeight;
    if (payload === undefined) {
      console.info('[teslawithme:geo-check]', label);
    } else {
      console.info('[teslawithme:geo-check]', label, payload);
    }
  };

  const currentOptions = (): PositionOptions => {
    const highAccuracy = highAccuracyInput.checked;
    return {
      enableHighAccuracy: highAccuracy,
      maximumAge: highAccuracy ? 5_000 : 600_000,
      timeout: highAccuracy ? 15_000 : 30_000,
    };
  };

  const updateStatus = (status: string, detail?: string): void => {
    statusEl.textContent = status;
    if (detail) {
      lastFixEl.textContent = detail;
    }
  };

  const handleSuccess = (source: 'getCurrentPosition' | 'watchPosition', pos: GeolocationPosition): void => {
    const payload = {
      source,
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      accuracy: pos.coords.accuracy,
      altitude: pos.coords.altitude,
      altitudeAccuracy: pos.coords.altitudeAccuracy,
      heading: pos.coords.heading,
      speed: pos.coords.speed,
      timestamp: pos.timestamp,
    };
    appendLog(`${source}.success`, payload);
    updateStatus(
      `Location fix acquired via ${source}`,
      `Lat ${pos.coords.latitude.toFixed(6)}, Lng ${pos.coords.longitude.toFixed(6)}, accuracy ${Math.round(pos.coords.accuracy)}m`,
    );
  };

  const handleError = (source: 'getCurrentPosition' | 'watchPosition', err: GeolocationPositionError): void => {
    const payload = {
      source,
      code: err.code,
      codeLabel: GEO_ERROR_LABELS[err.code] ?? 'UNKNOWN',
      message: err.message,
    };
    appendLog(`${source}.error`, payload);
    updateStatus(
      `${source} failed`,
      `code ${err.code} (${GEO_ERROR_LABELS[err.code] ?? 'UNKNOWN'})${err.message ? `: ${err.message}` : ''}`,
    );
  };

  const runOneShotCheck = (): void => {
    const options = currentOptions();
    updateStatus('Running one-shot check…');
    appendLog('getCurrentPosition.begin', { options });
    navigator.geolocation.getCurrentPosition(
      pos => handleSuccess('getCurrentPosition', pos),
      err => handleError('getCurrentPosition', err),
      options,
    );
  };

  const startWatch = (): void => {
    if (watchId !== null) return;
    const options = currentOptions();
    updateStatus('Starting watch…');
    appendLog('watchPosition.begin', { options });
    watchId = navigator.geolocation.watchPosition(
      pos => handleSuccess('watchPosition', pos),
      err => handleError('watchPosition', err),
      options,
    );
    appendLog('watchPosition.id', watchId);
    updateWatchButtons();
  };

  const stopWatch = (): void => {
    if (watchId === null) return;
    navigator.geolocation.clearWatch(watchId);
    appendLog('watchPosition.stop', { watchId });
    watchId = null;
    updateStatus('Watch stopped');
    updateWatchButtons();
  };

  const copyLog = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(logLines.join('\n'));
      updateStatus('Diagnostic log copied');
    } catch {
      updateStatus('Could not copy log', 'Clipboard access was not available.');
    }
  };

  const resetState = (): void => {
    stopWatch();
    clearTeslaWithMeBrowserState();
    logLines.length = 0;
    logEl.textContent = '';
    appendLog('browserState.reset', {
      note: 'TeslaWithMe local and session storage cleared for this origin.',
    });
    updateStatus(
      'App storage cleared',
      'If the browser does not show the location prompt again, reset Location for this site in browser settings.',
    );
    void inspectEnvironment();
  };

  const inspectEnvironment = async (): Promise<void> => {
    appendLog('environment', {
      isSecureContext: window.isSecureContext,
      hasGeolocation: 'geolocation' in navigator,
      userAgent: navigator.userAgent,
    });

    if (!('geolocation' in navigator)) {
      permissionEl.textContent = 'Permission: geolocation API unavailable';
      updateStatus('Browser geolocation unavailable');
      return;
    }

    if ('permissions' in navigator && navigator.permissions?.query) {
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' });
        permissionEl.textContent = `Permission: ${status.state}`;
        appendLog('permission', { state: status.state });
        status.onchange = () => {
          permissionEl.textContent = `Permission: ${status.state}`;
          appendLog('permission.change', { state: status.state });
        };
      } catch (error) {
        permissionEl.textContent = 'Permission: query failed';
        appendLog('permission.query.error', error);
      }
    } else {
      permissionEl.textContent = 'Permission: query unsupported';
      appendLog('permission', { state: 'unsupported' });
    }
  };

  backBtn.addEventListener('click', () => {
    stopWatch();
    onBack();
  });
  runOnceBtn.addEventListener('click', runOneShotCheck);
  startWatchBtn.addEventListener('click', startWatch);
  stopWatchBtn.addEventListener('click', stopWatch);
  copyLogBtn.addEventListener('click', () => {
    void copyLog();
  });
  resetStateBtn.addEventListener('click', resetState);

  updateWatchButtons();
  void inspectEnvironment();
}

function safeFormat(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
